import type { FilterOption } from '@/components/lw';
import { obConnectionState } from './obConnectionState';
import type { User } from './types';

export type UserManagementFilterFacet = 'roles' | 'states' | 'integrations';
export type UserManagementIntegrationFilter = 'connected' | 'attention' | 'not-set-up';
export type UserManagementStateMatchMode = 'any' | 'all';

export interface UserManagementFilters {
  roles: string[];
  states: string[];
  integrations: UserManagementIntegrationFilter[];
}

export interface UserManagementFilterOptions {
  roles: FilterOption[];
  states: FilterOption[];
  integrations: FilterOption[];
}

export const EMPTY_USER_MANAGEMENT_FILTERS: UserManagementFilters = {
  roles: [],
  states: [],
  integrations: []
};

const ATTENTION_PLAN_SCOPES = new Set(['full_footprint', 'custom_only', 'mixed']);

export const getUserIntegrationFilter = (user: User): UserManagementIntegrationFilter => {
  const hasEncompass = Boolean(user.encompassUserId);
  const optimalBlue = obConnectionState(
    user.optimalBlueUserId,
    user.optimalBlueBusinessChannelId
  );

  if (!hasEncompass && optimalBlue === 'not_connected') {
    return 'not-set-up';
  }
  if (
    hasEncompass &&
    optimalBlue === 'connected' &&
    !ATTENTION_PLAN_SCOPES.has(user.optimalBlueCurrentScheduleScope ?? '')
  ) {
    return 'connected';
  }
  return 'attention';
};

const countOptions = (
  values: readonly string[],
  labelForValue: (value: string) => string = (value) => value
): FilterOption[] => {
  const counts = new Map<string, number>();
  for (const value of values) {
    if (value) {
      counts.set(value, (counts.get(value) ?? 0) + 1);
    }
  }
  return [...counts.entries()]
    .map(([value, count]) => ({ value, label: labelForValue(value), count }))
    .sort((left, right) => left.label.localeCompare(right.label));
};

const INTEGRATION_LABELS: Record<UserManagementIntegrationFilter, string> = {
  connected: 'Connected',
  attention: 'Needs attention',
  'not-set-up': 'Not set up'
};

export const buildUserManagementFilterOptions = (
  users: readonly User[]
): UserManagementFilterOptions => ({
  roles: countOptions(users.flatMap((user) => user.roles?.length ? user.roles : ['No Role'])),
  states: countOptions(users.flatMap((user) => user.licensedStates)),
  integrations: countOptions(
    users.map(getUserIntegrationFilter),
    (value) => INTEGRATION_LABELS[value as UserManagementIntegrationFilter]
  )
});

export const applyUserManagementFilters = (
  users: readonly User[],
  filters: UserManagementFilters,
  stateMatchMode: UserManagementStateMatchMode = 'any'
): User[] => users.filter((user) => {
  const roles = user.roles?.length ? user.roles : ['No Role'];
  const matchesRole = filters.roles.length === 0 ||
    filters.roles.some((role) => roles.includes(role));
  const matchesState = filters.states.length === 0 || (
    stateMatchMode === 'all'
      ? filters.states.every((state) => user.licensedStates.includes(state))
      : filters.states.some((state) => user.licensedStates.includes(state))
  );
  const matchesIntegration = filters.integrations.length === 0 ||
    filters.integrations.includes(getUserIntegrationFilter(user));
  return matchesRole && matchesState && matchesIntegration;
});

export const hasUserManagementFilters = (filters: UserManagementFilters): boolean =>
  filters.roles.length > 0 || filters.states.length > 0 || filters.integrations.length > 0;

export const toggleUserManagementFilter = (
  filters: UserManagementFilters,
  facet: UserManagementFilterFacet,
  value: string
): UserManagementFilters => {
  const values = filters[facet] as string[];
  const nextValues = values.includes(value)
    ? values.filter((candidate) => candidate !== value)
    : [...values, value];
  return {
    ...filters,
    [facet]: nextValues
  } as UserManagementFilters;
};

export const clearUserManagementFilter = (
  filters: UserManagementFilters,
  facet: UserManagementFilterFacet
): UserManagementFilters => ({
  ...filters,
  [facet]: []
});
